import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button } from 'react-bootstrap';
import { Field as ReactField, useField, useForm } from 'react-final-form';
import { t } from 'react-switch-lang';
import { mailcheckValidate, validateEmail as validateContactEmail } from '../../pages/dynamicPages/contact/Validation';
import './FormFields.scss';

export function Label({
  id,
  name,
  label,
  optional = false,
  optionalText = t('ContactForm_Optional'),
  formatText = '',
}) {
  return (
    <label htmlFor={id} id={`${name}Label`} className="field-label">
      {label}
      {optional && (
        <span className="optional-lbl">{`(${optionalText})`}</span>
      )}
      {formatText && <p className="format-lbl">{formatText}</p>}
    </label>
  );
}

function getFieldState(meta) {
  if (!meta.touched && !meta.submitFailed) return '';
  return meta.error || (meta.submitError && !meta.dirtySinceLastSubmit) ? 'is-invalid' : 'is-valid';
}

function FieldError({ id, meta }) {
  const error = meta.error || meta.submitError;
  if ((!meta.touched && !meta.submitFailed) || !error) return null;

  return (
    <span id={`${id}Error`} className="error-txt invalid-feedback">
      {error}
    </span>
  );
}

export function TextField({
  id,
  name,
  label,
  placeholder = '',
  helpText = '',
  formatText = '',
  initialValue = '',
  format = undefined,
  activeFormat = true,
  parse = activeFormat ? format : undefined,
  autoComplete = '',
  className = '',
  fieldClassName = '',
  optional = false,
  optionalText,
  disabled = false,
  type = 'text',
  validate,
  onBlur,
  onChange,
  ...props
}) {
  const { input, meta } = useField(name, {
    initialValue,
    formatOnBlur: !!format,
    format,
    parse,
    validate,
  });

  const fieldState = getFieldState(meta);
  const inputClassNames = useMemo(() => ([
    fieldClassName,
    'form-control',
    fieldState,
    input.value === '' && 'is-empty',
  ].filter(Boolean).join(' ')), [fieldClassName, fieldState, input.value]);

  const handleBlur = useCallback((event) => {
    input.onBlur(event);
    onBlur?.(event);
  }, [input, onBlur]);

  const handleChange = useCallback((event) => {
    input.onChange(event);
    onChange?.(event);
  }, [input, onChange]);

  return (
    <div className={`${className} form-field mailcheck-email-field`}>
      <Label
        id={id}
        name={name}
        label={label}
        optional={optional}
        optionalText={optionalText}
        formatText={formatText}
      />
      <input
        {...input}
        id={id}
        name={name}
        type={type}
        disabled={disabled}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className={inputClassNames}
        aria-describedby={`${id}Error`}
        onBlur={handleBlur}
        onChange={handleChange}
        {...props}
      />
      <FieldError id={id} meta={meta} />
      {helpText && <p className="help-txt">{helpText}</p>}
    </div>
  );
}

export function MailcheckEmailField({
  id,
  name,
  label,
  placeholder = '',
  formatText = '',
  initialValue = '',
  format = undefined,
  activeFormat = true,
  parse = activeFormat ? format : undefined,
  autoComplete = '',
  className = '',
  fieldClassName = '',
  optional = false,
  optionalText,
  disabled = false,
  validate = validateContactEmail,
  buttonContainerClassName = 'mailcheck-email-actions',
  buttonClassName = 'clickable-btn inactive-btn',
  enableEmailSuggestion = true,
  onChange,
  ...props
}) {
  const form = useForm();
  const suggestionEnabled = useRef(enableEmailSuggestion);
  const [emailSuggestion, setEmailSuggestion] = useState(null);

  const validateEmail = (value, allValues) => {
    let error = validate?.(value || '', allValues);
    if (suggestionEnabled.current && !error) {
      const suggestedEmail = mailcheckValidate(value?.toLowerCase().trim());
      setEmailSuggestion(suggestedEmail);
      if (suggestedEmail) {
        error = t('Contact_EmailSuggestion').replace('xEmail', suggestedEmail);
      }
    } else {
      setEmailSuggestion(null);
    }

    return error;
  };

  const { input, meta } = useField(name, {
    initialValue,
    formatOnBlur: !!format,
    format,
    parse,
    validate: validateEmail,
  });

  const fieldState = getFieldState(meta);
  const inputClassNames = useMemo(() => ([
    fieldClassName,
    'form-control',
    fieldState,
    input.value === '' && 'is-empty',
  ].filter(Boolean).join(' ')), [fieldClassName, fieldState, input.value]);

  const handleEmailChange = useCallback((event) => {
    suggestionEnabled.current = true;
    input.onChange(event);
    onChange?.(event);
  }, [input, onChange]);

  return (
    <div className={`${className} form-field`}>
      <Label
        id={id}
        name={name}
        label={label}
        optional={optional}
        optionalText={optionalText}
        formatText={formatText}
      />
      <input
        {...input}
        id={id}
        name={name}
        disabled={disabled}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className={inputClassNames}
        inputMode="email"
        type="text"
        aria-describedby={`${id}Error`}
        onChange={handleEmailChange}
        {...props}
      />
      {fieldState === 'is-invalid' && (
        emailSuggestion ? (
          <>
            <span id={`${id}Error`} className="error-txt invalid-feedback">{meta.error}</span>
            <div className={buttonContainerClassName}>
              <Button
                text={t('MailCheck_ButtonYes')}
                type="button"
                fill="outline"
                className={buttonClassName}
                aria-label={t('Contact_EmailSuggestionYes').replace('xEmail', emailSuggestion)}
                onClick={() => {
                  setEmailSuggestion(null);
                  form.change(name, emailSuggestion);
                }}
              >
                {t('Yes_Text')}
              </Button>
              <Button
                text={t('MailCheck_ButtonNo')}
                type="button"
                fill="outline"
                className={buttonClassName}
                aria-label={t('Contact_EmailSuggestionNo')}
                onClick={() => {
                  setEmailSuggestion(null);
                  suggestionEnabled.current = false;
                  form.resetFieldState(name);
                  form.focus(name);
                  form.blur(name);
                }}
              >
                {t('No_Text')}
              </Button>
            </div>
          </>
        ) :
          <FieldError id={id} meta={meta} />
      )}
    </div>
  );
}

export function TextAreaField({
  id,
  name,
  label,
  placeholder = '',
  helpText = '',
  formatText = '',
  initialValue = '',
  format = undefined,
  parse = format,
  autoComplete = '',
  className = '',
  fieldClassName = '',
  optional = false,
  optionalText,
  disabled = false,
  validate,
  onBlur,
  onChange,
  children,
  ...props
}) {
  return (
    <ReactField
      name={name}
      initialValue={initialValue}
      formatOnBlur={!!format}
      format={format}
      parse={parse}
      validate={validate}
    >
      {({ input, meta }) => {
        const fieldState = getFieldState(meta);
        const handleBlur = (event) => {
          input.onBlur(event);
          onBlur?.(event);
        };
        const handleChange = (event) => {
          input.onChange(event);
          onChange?.(event);
        };

        return (
          <div className={`${className} form-field text-area-field`}>
            <Label
              id={id}
              name={name}
              label={label}
              optional={optional}
              optionalText={optionalText}
              formatText={formatText}
            />
            {children}
            <textarea
              {...input}
              id={id}
              name={name}
              disabled={disabled}
              placeholder={placeholder}
              autoComplete={autoComplete}
              className={`${fieldClassName} text-area-input form-control ${fieldState} ${input.value === '' ? 'is-empty' : ''}`}
              aria-describedby={`${id}Error`}
              onBlur={handleBlur}
              onChange={handleChange}
              {...props}
            />
            <FieldError id={id} meta={meta} />
            {helpText && <p className="help-txt">{helpText}</p>}
          </div>
        );
      }}
    </ReactField>
  );
}

export function SelectField({
  id,
  name,
  label,
  placeholder = '',
  placeholderAriaLabel,
  helpText = '',
  className = '',
  optional = false,
  optionalText,
  disabled = false,
  options = [],
  validate,
  onBlur,
  onChange,
  ...props
}) {
  return (
    <ReactField name={name} validate={validate}>
      {({ input, meta }) => {
        const fieldState = getFieldState(meta);
        const handleBlur = (event) => {
          input.onBlur(event);
          onBlur?.(event);
        };
        const handleChange = (event) => {
          input.onChange(event);
          onChange?.(event);
        };

        return (
          <div className={`${className} form-field`}>
            <Label
              id={id}
              name={name}
              label={label}
              optional={optional}
              optionalText={optionalText}
            />
            <select
              {...input}
              id={id}
              name={name}
              disabled={disabled}
              className={`form-select ${fieldState} ${input.value === '' ? 'is-empty' : ''}`}
              aria-describedby={`${id}Error`}
              onBlur={handleBlur}
              onChange={handleChange}
              {...props}
            >
              <option value="" aria-label={placeholderAriaLabel || placeholder}>
                {placeholder}
              </option>
              {options.map(({ label: optionLabel, value }) => (
                <option value={value} key={value}>
                  {optionLabel}
                </option>
              ))}
            </select>
            <FieldError id={id} meta={meta} />
            {helpText && <p className="help-txt">{helpText}</p>}
          </div>
        );
      }}
    </ReactField>
  );
}

export function ContactRadioField({
  name,
  value,
  content,
  className = '',
  optionClassName = '',
  fixAlignment = false,
  disabled = false,
  onChange,
}) {
  const { input } = useField(name, { type: 'radio', value });

  return (
    // eslint-disable-next-line jsx-a11y/label-has-associated-control
    <label className={`card radio-card ${className}`.trim()}>
      <input
        name={input.name}
        value={value}
        className="contact-radio"
        type="radio"
        checked={input.checked}
        disabled={disabled}
        onBlur={input.onBlur}
        onChange={() => {
          input.onChange(value);
          input.onBlur();
          onChange?.(value, input);
        }}
      />
      <span className={`select-option ${optionClassName} ${fixAlignment ? 'fixAlignment' : ''}`.trim()}>{content}</span>
    </label>
  );
}
